import { memo } from "react";

import {
  Badge,
  Button,
  Card,
  Group,
  Skeleton,
  Stack,
  Text
} from "@mantine/core";
import { TbBrain, TbCheck, TbDeviceFloppy } from "react-icons/tb";

import { useSingleBrainstormIdeaQuery } from "modules/collaboration/hooks/queries/useSingleBrainstormIdeaQuery";
import { useModalsStore } from "../../ChatModals/modals.store";

export type DisplayDraftBrainstormIdeaProps = {
  idea: {
    id: string;
    title: string;
    description: string;
  };
};

const DisplayDraftBrainstormIdea = (props: DisplayDraftBrainstormIdeaProps) => {
  const { idea } = props;

  const openModal = useModalsStore(state => state.openModal);

  return (
    <MemoizedDraftBrainstormIdea
      key={idea.id}
      {...idea}
      openSaveModal={() =>
        openModal("brainstormSaveDraftModal", {
          id: idea.id,
          title: idea.title,
          description: idea.description
        })
      }
    />
  );
};

const DraftBrainstormIdea = ({
  id,
  title,
  description,
  openSaveModal
}: {
  id: string;
  title: string;
  description: string;
  openSaveModal: () => void;
}) => {
  const { data: idea, isLoading } = useSingleBrainstormIdeaQuery(id, {
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  if (isLoading) {
    return (
      <Card>
        <Skeleton height={16} width="100%" mb={4} />
        <Skeleton height={16} width="70%" />
      </Card>
    );
  }

  const isSaved = !!idea;
  const color = isSaved ? "green" : "violet";

  return (
    <Card
      withBorder
      bg={`${color}.0`}
      bd={`1px solid var(--mantine-color-${color}-2)`}
      shadow="xs"
      radius="lg"
    >
      <Stack gap="xs">
        <Badge
          leftSection={<TbBrain size={12} />}
          color="violet"
          size="sm"
          tt="capitalize"
          variant="transparent"
        >
          Brainstorm Idea Draft
        </Badge>

        <div>
          <Text fz="sm" fw={700}>
            {idea?.title ?? title}
          </Text>

          <Text fz="sm" c="gray.6">
            {idea?.description ?? description}
          </Text>
        </div>

        <Group justify="flex-end">
          {idea ? (
            <Badge
              color="green"
              leftSection={<TbCheck />}
              variant="light"
              size="xs"
            >
              Saved
            </Badge>
          ) : (
            <Button
              size="xs"
              leftSection={<TbDeviceFloppy />}
              onClick={openSaveModal}
            >
              Edit & Save
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
};

const MemoizedDraftBrainstormIdea = memo(DraftBrainstormIdea);

export default memo(DisplayDraftBrainstormIdea);
